import React, { useRef, useCallback, useEffect, useState, useMemo } from 'react';
import { Box, Typography, Chip, Fade } from '@mui/material';
import { Psychology } from '@mui/icons-material';

interface ThinkingSectionProps {
  showThinking: boolean;
  thinkingText: string;
}

const NEAR_BOTTOM_THRESHOLD_PX = 28;
const TYPING_TICK_MS = 35;
const MAX_CHARS_PER_TICK = 5;
const REMAINING_DIVISOR = 20;

export const ThinkingSection: React.FC<ThinkingSectionProps> = ({ 
  showThinking, 
  thinkingText 
}) => {
  const [visibleLength, setVisibleLength] = useState(thinkingText.length);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const shouldStickToBottomRef = useRef(true);

  const isNearBottom = useCallback((element: HTMLDivElement) => {
    const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
    return distanceFromBottom <= NEAR_BOTTOM_THRESHOLD_PX;
  }, []);

  useEffect(() => {
    if (thinkingText.length < visibleLength) {
      setVisibleLength(thinkingText.length);
      return;
    }

    if (!showThinking) {
      setVisibleLength(thinkingText.length);
      return;
    }

    if (visibleLength >= thinkingText.length) return;

    let timeoutId: number | undefined;

    const tick = () => {
      setVisibleLength((current) => {
        if (current >= thinkingText.length) return current;
        const remaining = thinkingText.length - current;
        const step = Math.min(Math.max(1, Math.ceil(remaining / REMAINING_DIVISOR)), MAX_CHARS_PER_TICK);
        return Math.min(thinkingText.length, current + step);
      });
      timeoutId = window.setTimeout(tick, TYPING_TICK_MS);
    };

    timeoutId = window.setTimeout(tick, TYPING_TICK_MS);

    return () => {
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [thinkingText, showThinking, visibleLength]);

  const visibleText = useMemo(() => {
    if (!showThinking) return thinkingText;
    return thinkingText.slice(0, visibleLength);
  }, [showThinking, thinkingText, visibleLength]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    if (shouldStickToBottomRef.current) {
      container.scrollTop = container.scrollHeight;
    }
  }, [visibleText]);

  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    shouldStickToBottomRef.current = isNearBottom(event.currentTarget);
  }, [isNearBottom]);

  if (!showThinking || !thinkingText) return null;

  const isStreaming = visibleLength < thinkingText.length;

  return (
    <Fade in={showThinking} timeout={500}>
      <Box sx={{
        mt: 2,
        p: 2,
        bgcolor: '#f8fafc',
        borderRadius: 2,
        border: '1px solid #e2e8f0',
        boxShadow: '0 2px 6px rgba(15, 23, 42, 0.04)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Psychology sx={{ color: '#6366f1', fontSize: '1.25rem' }} />
          <Typography
            variant="subtitle2"
            sx={{
              color: '#1f2937',
              fontWeight: 600,
              fontSize: '0.875rem'
            }}
          >
            AI Reasoning Process
          </Typography>
          <Chip
            label="Live"
            size="small"
            sx={{
              backgroundColor: '#fef9c3',
              color: '#854d0e',
              fontWeight: 600,
              fontSize: '0.65rem',
              height: 18,
              border: '1px solid #fde68a'
            }}
          />
        </Box>
        <Box
          ref={scrollContainerRef}
          onScroll={handleScroll}
          sx={{
            color: '#334155',
            fontSize: '0.8rem',
            fontFamily: 'monospace',
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap',
            backgroundColor: '#f1f5f9',
            p: 1.5,
            borderRadius: 1.5,
            border: '1px solid #e2e8f0',
            maxHeight: '150px',
            overflowY: 'auto',
            '@keyframes streamingCaretBlink': {
              '0%, 45%': { opacity: 1 },
              '55%, 100%': { opacity: 0.15 },
            },
          }}
        >
          {visibleText}
          {isStreaming && (
            <Box
              component="span"
              aria-hidden="true"
              sx={{
                display: 'inline-block',
                width: '0.7ch',
                height: '1.15em',
                ml: '1px',
                verticalAlign: 'text-bottom',
                borderRadius: '2px',
                background: 'linear-gradient(180deg, rgba(44,192,183,0.95), rgba(103,232,249,0.85))',
                boxShadow: '0 0 12px rgba(44,192,183,0.25)',
                animation: 'streamingCaretBlink 0.95s ease-in-out infinite',
              }}
            />
          )}
        </Box>
      </Box>
    </Fade>
  );
}; 